// 3rd Party
import Analytics from '@opencare/analytics';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import { H1, P } from 'common/styles/oc-styled-components';

// Other
import { colors } from 'common/styles/style-constants';
import { updateCheckoutErrorStatus } from 'connect/actions';
import { CHECKOUT_ERROR_TYPES } from 'connect/constants';
import {
  selectDataLoadingUser,
  selectDataRequestedAppointment,
  selectDataUser,
} from 'connect/selectors';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useRedirectionService } from 'services/RedirectionService';

import { PARTNER_PATH } from '../../../../../containers/App/Routes';
import Error from '../../../../common/components/Error/Error';
import { StepLoading, StyledLock } from '../../../checkout-styled-components';
import { selectDataCheckoutFormErrorStatus } from '../../../connect/selectors';
import PatientForm from '../PatientForm/PatientForm';

const analytics = new Analytics();

// Component
const PatientStep = ({ handleNextStep, state }) => {
  const dispatch = useDispatch();

  const analyticsService = useAnalyticsService();
  const redirectionService = useRedirectionService();

  const checkoutFormErrorStatus = useSelector(
    selectDataCheckoutFormErrorStatus,
  );

  const loadingUser = useSelector(selectDataLoadingUser);
  const user = useSelector(selectDataUser);
  const requestedAppointment = useSelector(selectDataRequestedAppointment);

  const [isLoading, setIsLoading] = useState(true);

  // All necessary data is loaded
  useEffect(() => {
    if (user && state.isCleared) {
      // Cached data got cleared, redirect back to Partner Page
      redirectionService.redirect({
        url: PARTNER_PATH,
        reason: 'patient-step-state-cleared',
      });
    }
  }, [user, state]);

  useEffect(() => {
    if (user && requestedAppointment && !loadingUser && !state.isLoading) {
      setIsLoading(false);
    }
  }, [user, requestedAppointment, loadingUser, state]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const trackData = {
      pageType: !checkoutFormErrorStatus
        ? analytics.pageTypes.FORM
        : analytics.pageTypes.FORM_ERROR,
      pageName: 'partner_form-booking-account',
      name: 'form-booking',
      section: 'create-patient',
      userId: user.id,
      partnerId: user.partner.id,
      practiceId: _.get(requestedAppointment, 'provider.clinic'),
      providerId: _.get(requestedAppointment, 'providerId'),
      patientId: state.id,
      ...analyticsService.analyticsData,
    };

    if (
      checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED
    ) {
      trackData.content = { 'error-already-registered': 'true' };
    }

    if (checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.EMAIL_INVALID) {
      trackData.content = { 'error-email-invalid': 'true' };
    }

    // Initial analytics page view event, track errors if any
    analytics.trackPageView(trackData);
  }, [user, checkoutFormErrorStatus]);

  useEffect(() => {
    if (
      [
        CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED,
        CHECKOUT_ERROR_TYPES.EMAIL_INVALID,
      ].includes(checkoutFormErrorStatus)
    ) {
      dispatch(updateCheckoutErrorStatus(null));
    }
  }, [user]);

  const renderErrorMessage = () => {
    if (!checkoutFormErrorStatus) {
      return null;
    }

    let error = null;
    if (checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.GENERIC) {
      error = 'Something went wrong while submitting your details.';
    }
    if (checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.EMAIL_INVALID) {
      error = 'This email address is invalid, please check for the typos.';
    }
    if (
      checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.EMAIL_ALREADY_REGISTERED
    ) {
      error = 'This email has already been registered.';
    }

    if (error === null) {
      return null;
    }

    return (
      <Error dataTestId="patientStepError" style={{ marginBottom: '30px' }}>
        <P color={colors.danger} data-testid={checkoutFormErrorStatus}>
          {checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.GENERIC
          && <>{error}</>}
        </P>
      </Error>
    );
  };

  if (isLoading) {
    return (
      <div data-testid="loading">
        <StepLoading type="loading" />
      </div>
    );
  }

  return (
    <div>
      {renderErrorMessage()}
      <H1
        data-testid="patientStepHeading"
        fontSize="20px"
        margin="-12px 0 0 auto"
      >
        Create Patient Account
        <StyledLock
          aria-hidden
          bottom="5px"
          margin="0 0 0 8px"
          size="16px"
          theme="filled"
          type="lock"
          role="presentation"
        />
      </H1>
      <P color={colors.slate} margin="0 0 16px">
        By doing so, we can keep their information secure.
      </P>
      <PatientForm handleNextStep={handleNextStep} state={state} />
    </div>
  );
};

PatientStep.propTypes = {
  handleNextStep: PropTypes.func,
  state: PropTypes.object,
};

export default PatientStep;
